package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import kotlinx.coroutines.flow.Flow

/**
 * User↔business chat over `conversations/{uid}_{businessId}` + its `messages` subcollection (rules:
 * participants only). Chat requires a **real (non-anonymous) account** — guests get
 * [diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unauthorized] and the UI routes to
 * sign-in — and a **claimed** business (an `ownerUid` to talk to).
 */
/** Rules cap `publicProfiles/{uid}.photos` at 6; the client mirrors that so a write never bounces. */
const val MAX_PROFILE_PHOTOS = 6

interface ChatRepository {
    /**
     * Uid of the signed-in (non-anonymous) user, or null for guests. Lets callers resolve the thread
     * counterparty (`participantUids` minus self) for trust/safety actions (report/block).
     */
    val currentUid: String?

    /** Streams the signed-in user's conversations, most recent activity first. */
    fun observeConversations(): Flow<DataResult<List<Conversation>>>

    /** Streams a conversation's messages, oldest first ([Message.isMine] resolved to the caller). */
    fun observeMessages(conversationId: String): Flow<DataResult<List<Message>>>

    /** One-shot read of a thread (header identity when opened from the inbox). */
    suspend fun getConversation(conversationId: String): DataResult<Conversation>

    /**
     * Returns the caller's conversation id with [business], creating the thread on first contact.
     * Fails with `Unauthorized` for guests and `NotFound` when the business has no owner to reach.
     */
    suspend fun ensureConversation(business: Business): DataResult<String>

    /**
     * Returns the caller's buyer↔seller thread about [item] (`item_{itemId}_{buyerUid}` — one per
     * buyer per listing), creating it on first contact with the item title as the thread's display
     * title. Fails with `Unauthorized` for guests; sellers can't message themselves.
     */
    suspend fun ensureItemConversation(item: MarketplaceItem): DataResult<String>

    /**
     * Returns the caller's **direct-message** thread with [other] (`dm_{minUid}_{maxUid}` — sorted
     * pair, one per user pair), creating it on first contact. The new thread is a message REQUEST for
     * the recipient (accepted only by the caller) until they accept or reply. Fails with
     * `Unauthorized` for guests; you can't DM yourself.
     */
    suspend fun ensureDmConversation(other: PublicProfile): DataResult<String>

    /**
     * Creates a **group** thread (`group_{id}`) named [title] with the caller + [members]
     * (2–19 others). The caller is the only accepted member; everyone else gets it as a message
     * REQUEST (Requests inbox) until they accept — the same anti-spam model as DMs.
     */
    suspend fun createGroupConversation(title: String, members: List<PublicProfile>): DataResult<String>

    /** Accept a pending message request — adds the caller to the thread's `acceptedUids`. */
    suspend fun acceptConversation(conversationId: String): DataResult<Unit>

    /** Decline a pending request — deletes the thread (the requester can start over if they wish). */
    suspend fun declineConversation(conversationId: String): DataResult<Unit>

    /** Leave a group (or decline its invite) — removes only the caller; the group lives on. */
    suspend fun leaveConversation(conversationId: String): DataResult<Unit>

    /**
     * Finds users to message by display-name prefix (case-insensitive, `publicProfiles`), excluding
     * the caller. Empty query returns no results.
     */
    suspend fun searchUsers(query: String): DataResult<List<PublicProfile>>

    /**
     * Streams the opt-in Find-your-people directory (discoverable profiles, name order), excluding
     * the caller. [intent] filters client-side to a connection intent (null = all).
     */
    fun observeDiscoverablePeople(intent: String? = null, limit: Int = 50): Flow<DataResult<List<PublicProfile>>>

    /** One-shot read of the caller's own public profile (discovery editor prefill). */
    suspend fun myPublicProfile(): DataResult<PublicProfile?>

    /** One-shot read of any member's public profile (person profile screen). Null when absent. */
    suspend fun publicProfile(uid: String): DataResult<PublicProfile?>

    /**
     * Saves the caller's Find-your-people card: [discoverable] opt-in/out, short [bio], and intent
     * keys. Merges into `publicProfiles/{uid}` (display name stays mirrored from the profile).
     */
    suspend fun setDiscoveryProfile(discoverable: Boolean, bio: String?, intents: List<String>): DataResult<Unit>

    /**
     * Checks the caller IN to the people MAP at a city area (~11 km grid) or neighbourhood (~2 km grid)
     * for [diana.soleil.design.iraniancanadienbazzar.core.model.PEOPLE_MAP_CHECK_IN_MILLIS], or checks
     * them out with [precision] Hidden. Every check-in is manual and expires — there is no standing
     * "always show me" state (App Store 5.1.2). Coordinates are snapped BEFORE storage; a precise
     * position is never written. A visible level needs both coordinates.
     */
    suspend fun setMapVisibility(
        precision: diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision,
        lat: Double?,
        lng: Double?,
    ): DataResult<Unit>

    /**
     * Saves the caller's public self-disclosure fields (person-profile screen). All optional —
     * blanks clear. Merges into `publicProfiles/{uid}`. [city] is where they live now (≤60 chars).
     */
    suspend fun setSocialProfile(
        city: String?,
        occupation: String?,
        hometown: String?,
        relationship: String?,
        languages: List<String>,
        interests: List<String>,
    ): DataResult<Unit>

    /**
     * Uploads [image] as the caller's profile photo (`users/{uid}/` Storage, public-read) and
     * merges the resulting URL into `publicProfiles/{uid}.photoUrl`. Returns the URL.
     */
    suspend fun setProfilePhoto(image: diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage): DataResult<String>

    /**
     * Append a photo to the caller's public gallery (`publicProfiles/{uid}.photos`).
     *
     * Capped at [MAX_PROFILE_PHOTOS] to match the Firestore rule (`photos.size() <= 6`) — the client
     * refuses past the cap rather than letting the write bounce.
     */
    suspend fun addProfilePhoto(image: diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage): DataResult<String>

    /** Remove one photo from the caller's public gallery by its download URL. */
    suspend fun removeProfilePhoto(url: String): DataResult<Unit>

    /** Appends a message and updates the thread's `lastMessage`/`lastMessageAt` preview. */
    suspend fun sendMessage(conversationId: String, text: String): DataResult<Unit>
}
